'use client';

import { CaretDown, DotsThree } from '@phosphor-icons/react/ssr';
import { Slot } from 'radix-ui';
import {
  cloneElement,
  type ComponentProps,
  createContext,
  type ElementType,
  isValidElement,
  type ReactElement,
  type ReactNode,
  useContext,
  useId,
  useState,
} from 'react';

import { tv } from '../../lib/tv';
import { cn } from '../../lib/utils';
import { DropdownMenuItem } from '../ui/dropdown-menu';
import { Avatar, type AvatarStatus } from './avatar';
import { Badge } from './badge';
import { IconButton } from './icon-button';
import { Logo } from './logo';
import { Menu, MenuContent, MenuTrigger } from './menu';
import { Tooltip } from './tooltip';

type SidebarState = { collapsed: boolean };
const SidebarContext = createContext<SidebarState>({ collapsed: false });
/** Items inside a section render their own `<li>`; inside a collapsed group's Menu, a menu item. */
const PlacementContext = createContext<'list' | 'menu' | null>(null);

function useCollapsed(prop?: boolean) {
  const { collapsed } = useContext(SidebarContext);
  return prop ?? collapsed;
}

/** A Phosphor icon (takes `weight`), or any component taking className. */
type NavIcon = ElementType<{ className?: string; weight?: 'regular' | 'fill' }>;

export type SidebarProps = ComponentProps<'div'> & {
  /**
   * Figma "Collapsed": the 64px icon rail (labels in right Tooltips, section
   * labels become dividers). Figma: collapsed below 1280 or on user toggle -
   * the app owns the state.
   */
  collapsed?: boolean;
};

/**
 * App navigation (Figma: Components ↳ Sidebar, 61:1000). Expanded 240
 * (padding 8), Collapsed 64 (padding 12, centred), children 2px apart on
 * bg/surface with a 1px border/subtle right stroke. Compose:
 * `SidebarHeader` (Workspace Switcher) · `SidebarContent` (the named `<nav>`,
 * `SidebarSection`s of `NavItem` / `NavGroup`) · `SidebarFooter` (an upgrade
 * Well, `SidebarDivider`, `SidebarUser`). Tooltips need the app's
 * `TooltipProvider`.
 */
export function Sidebar({ collapsed = false, className, ...props }: SidebarProps) {
  return (
    <SidebarContext value={{ collapsed }}>
      <div
        data-slot="sidebar"
        data-collapsed={collapsed || undefined}
        className={cn(
          'group/sidebar bg-bg-surface flex h-full w-60 shrink-0 flex-col gap-0.5 p-2',
          // Figma INSIDE right stroke: no layout space, items stay 224 wide.
          'shadow-[inset_-1px_0_0_0_var(--color-border-subtle)]',
          'data-collapsed:w-16 data-collapsed:items-center data-collapsed:p-3',
          'motion-safe:transition-[width,padding] motion-safe:duration-200 motion-safe:ease-out',
          className,
        )}
        {...props}
      />
    </SidebarContext>
  );
}

/** Divider between sidebar regions: 17 tall, a 1px border/subtle line 8px from each edge. */
export function SidebarDivider({ className }: { className?: string }) {
  return (
    <div
      aria-hidden
      data-slot="sidebar-divider"
      className={cn('flex h-[17px] w-full shrink-0 items-center', className)}
    >
      <span className="bg-border-subtle h-px w-full" />
    </div>
  );
}

/** Top of the sidebar: the Workspace Switcher, then a divider. */
export function SidebarHeader({ className, children, ...props }: ComponentProps<'div'>) {
  return (
    <div
      data-slot="sidebar-header"
      className={cn(
        'flex w-full flex-col gap-0.5 group-data-collapsed/sidebar:items-center',
        className,
      )}
      {...props}
    >
      {children}
      <SidebarDivider />
    </div>
  );
}

/**
 * The navigation: a `<nav>` landmark ("Main" unless you pass `aria-label`)
 * that fills the height and scrolls when it must.
 */
export function SidebarContent({
  className,
  'aria-label': ariaLabel = 'Main',
  ...props
}: ComponentProps<'nav'>) {
  return (
    <nav
      aria-label={ariaLabel}
      data-slot="sidebar-content"
      className={cn(
        'flex min-h-0 w-full flex-1 flex-col gap-0.5 overflow-y-auto',
        'group-data-collapsed/sidebar:items-center',
        className,
      )}
      {...props}
    />
  );
}

/** Bottom of the sidebar (upgrade Well, divider, Sidebar User). */
export function SidebarFooter({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      data-slot="sidebar-footer"
      className={cn(
        'flex w-full flex-col gap-0.5 group-data-collapsed/sidebar:items-center',
        className,
      )}
      {...props}
    />
  );
}

export type NavSectionLabelProps = ComponentProps<'div'>;

/**
 * Figma Nav Section Label (61:66): 28 tall, Overline text/tertiary sitting 6px
 * above the bottom, 12px in. Hidden when the sidebar is collapsed (kept for
 * screen readers).
 */
export function NavSectionLabel({ className, ...props }: NavSectionLabelProps) {
  return (
    <div
      data-slot="nav-section-label"
      className={cn(
        'text-overline text-text-tertiary flex h-7 w-full shrink-0 items-end pb-1.5 pl-3 uppercase',
        'group-data-collapsed/sidebar:sr-only',
        className,
      )}
      {...props}
    />
  );
}

export type SidebarSectionProps = Omit<ComponentProps<'div'>, 'title'> & {
  /** Section title (Nav Section Label). Omit for an untitled group. */
  label?: ReactNode;
};

/**
 * A titled group of nav items (`role="group"` named by its label, items in a
 * list). Collapsed, the label is replaced by a divider - except for the
 * first section, which already sits under the header's divider (Figma).
 */
export function SidebarSection({
  label,
  className,
  children,
  ...props
}: SidebarSectionProps) {
  const id = useId();
  return (
    <div
      role="group"
      aria-labelledby={label ? id : undefined}
      data-slot="sidebar-section"
      className={cn(
        'flex w-full flex-col gap-0.5 group-data-collapsed/sidebar:items-center',
        'group-data-collapsed/sidebar:not-first:[&>[data-slot=sidebar-divider]]:flex',
        className,
      )}
      {...props}
    >
      {label && <NavSectionLabel id={id}>{label}</NavSectionLabel>}
      <SidebarDivider className="hidden" />
      <ul className="flex w-full flex-col gap-0.5 group-data-collapsed/sidebar:items-center">
        <PlacementContext value="list">{children}</PlacementContext>
      </ul>
    </div>
  );
}

function ListItem({ children }: { children: ReactNode }) {
  const placement = useContext(PlacementContext);
  return placement === 'list' ? (
    <li className="flex w-full justify-center">{children}</li>
  ) : (
    children
  );
}

const navItem = tv({
  slots: {
    root: [
      'group/nav relative flex h-9 w-full shrink-0 items-center gap-3 rounded-md pr-2 pl-3 text-left outline-none',
      'text-body-md text-text-secondary cursor-pointer',
      'transition-colors duration-[120ms] ease-out',
      'hover:bg-bg-muted hover:text-text-primary',
      'data-active:bg-brand-subtle data-active:text-label-md data-active:text-text-primary',
      'focus-visible:ring-2 focus-visible:ring-border-focus focus-visible:ring-inset',
      'aria-disabled:text-text-disabled aria-disabled:pointer-events-none aria-disabled:cursor-default',
      'data-[state=open]:bg-bg-muted',
    ],
    icon: [
      'size-5 shrink-0 text-icon-secondary',
      'group-hover/nav:text-icon-primary group-data-active/nav:text-brand-strong group-aria-disabled/nav:text-icon-tertiary',
    ],
    label: 'min-w-0 flex-1 truncate',
    chevron: 'text-icon-tertiary size-4 shrink-0 transition-transform duration-[120ms]',
    dot: 'bg-status-danger-solid ring-bg-surface absolute top-1.5 left-[26px] size-2 rounded-full ring-2',
  },
  variants: {
    collapsed: {
      true: { root: 'size-10 justify-center gap-0 p-0', label: 'sr-only' },
    },
  },
});

function RailTooltip({
  collapsed,
  content,
  children,
}: {
  collapsed: boolean;
  content: ReactNode;
  children: ReactElement;
}) {
  if (!collapsed) return children;
  return (
    <Tooltip side="right" content={content}>
      {children}
    </Tooltip>
  );
}

export type NavItemProps = Omit<ComponentProps<'a'>, 'children'> & {
  /** Phosphor icon (20px). Active renders its Fill weight - or `activeIcon`. */
  icon: NavIcon;
  /** Icon for the Active state, when it isn't `icon` at Fill weight. */
  activeIcon?: ElementType<{ className?: string }>;
  /** The page you are on: Pale Sage fill, SemiBold label, `aria-current="page"`. */
  active?: boolean;
  /** Figma "Count": a small Badge for unread / new items (Brand when Active). */
  count?: ReactNode;
  /** Figma "Dot": an unread dot on the collapsed icon. Defaults on when there's a count. */
  dot?: boolean;
  /** Figma State=Disabled: not a link, `aria-disabled`. */
  disabled?: boolean;
  /** Override the sidebar's collapsed state (e.g. a lone item in docs). */
  collapsed?: boolean;
  /** Render the single child (a router Link) as the item; its children are the label. */
  asChild?: boolean;
  /** Label. */
  children: ReactNode;
};

/**
 * Figma Nav Item (61:55): a sidebar link. Default text/secondary with an
 * icon/secondary icon; Hover bg/muted, primary; Active brand/subtle with a
 * brand/strong Fill icon and Label/Medium; Disabled text/disabled. Collapsed:
 * 40×40, icon only, the label in a right Tooltip (and still the link's name).
 */
export function NavItem({
  icon: Icon,
  activeIcon: ActiveIcon,
  active = false,
  count,
  dot,
  disabled = false,
  collapsed: collapsedProp,
  asChild = false,
  className,
  children,
  href,
  ...props
}: NavItemProps) {
  const collapsed = useCollapsed(collapsedProp);
  const styles = navItem({ collapsed });
  const hasCount = count !== undefined && count !== null && count !== false;
  const showDot = collapsed && (dot ?? hasCount);
  const content = (label: ReactNode) => (
    <>
      {active && ActiveIcon ? (
        <ActiveIcon aria-hidden data-slot="nav-icon" className={styles.icon()} />
      ) : (
        <Icon
          aria-hidden
          data-slot="nav-icon"
          weight={active ? 'fill' : 'regular'}
          className={styles.icon()}
        />
      )}
      <span
        data-slot="nav-label"
        data-disabled={disabled || undefined}
        className={styles.label()}
      >
        {label}
      </span>
      {hasCount && (
        <Badge
          tone={active ? 'brand' : 'neutral'}
          data-slot="nav-count"
          className={cn(collapsed && 'sr-only')}
        >
          {count}
        </Badge>
      )}
      {showDot && <span aria-hidden data-slot="nav-dot" className={styles.dot()} />}
    </>
  );
  const child =
    asChild && isValidElement<{ children?: ReactNode }>(children) ? children : null;
  const shared = {
    'data-slot': 'nav-item',
    'data-active': active || undefined,
    'aria-current': active ? ('page' as const) : undefined,
    'aria-disabled': disabled || undefined,
    className: styles.root({ className }),
    ...props,
  };
  const element = child ? (
    <Slot.Root {...shared}>
      {cloneElement(child, undefined, content(child.props.children))}
    </Slot.Root>
  ) : (
    <a
      href={disabled ? undefined : href}
      role={disabled ? 'link' : undefined}
      {...shared}
    >
      {content(children)}
    </a>
  );
  return (
    <ListItem>
      <RailTooltip
        collapsed={collapsed && !disabled}
        content={child ? child.props.children : children}
      >
        {element}
      </RailTooltip>
    </ListItem>
  );
}

export type NavGroupProps = {
  icon: NavIcon;
  /** Parent label ("Flows"). */
  label: ReactNode;
  /** A sub item is the current page: the parent shows Active while its list is hidden. */
  active?: boolean;
  count?: ReactNode;
  /** Sub list shown (controlled). */
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  collapsed?: boolean;
  className?: string;
  /** `NavSubItem`s. */
  children: ReactNode;
};

/**
 * A Nav Item with sub-items (Figma "Has sub-items": the chevron). Expanded it
 * is a disclosure button (`aria-expanded`) over a nested list of
 * `NavSubItem`s. Collapsed, the rail can't show the list: the icon opens a
 * Menu of the sub items to its right.
 */
export function NavGroup({
  icon: Icon,
  label,
  active = false,
  count,
  open: openProp,
  defaultOpen = false,
  onOpenChange,
  collapsed: collapsedProp,
  className,
  children,
}: NavGroupProps) {
  const collapsed = useCollapsed(collapsedProp);
  const styles = navItem({ collapsed });
  const listId = useId();
  const [innerOpen, setInnerOpen] = useState(defaultOpen);
  const open = openProp ?? innerOpen;
  const setOpen = (next: boolean) => {
    if (openProp === undefined) setInnerOpen(next);
    onOpenChange?.(next);
  };
  const showActive = active && (collapsed || !open);
  const icon = (
    <Icon
      aria-hidden
      data-slot="nav-icon"
      weight={showActive ? 'fill' : 'regular'}
      className={styles.icon()}
    />
  );

  if (collapsed) {
    return (
      <ListItem>
        <Menu>
          <RailTooltip collapsed content={label}>
            <MenuTrigger asChild>
              <button
                type="button"
                data-slot="nav-item"
                data-active={showActive || undefined}
                className={styles.root({ className })}
              >
                {icon}
                <span data-slot="nav-label" className={styles.label()}>
                  {label}
                </span>
              </button>
            </MenuTrigger>
          </RailTooltip>
          <MenuContent side="right" align="start" sideOffset={8}>
            <PlacementContext value="menu">{children}</PlacementContext>
          </MenuContent>
        </Menu>
      </ListItem>
    );
  }

  return (
    <ListItem>
      <div className="flex w-full flex-col gap-0.5">
        <button
          type="button"
          data-slot="nav-item"
          data-active={showActive || undefined}
          aria-expanded={open}
          aria-controls={listId}
          onClick={() => setOpen(!open)}
          className={styles.root({ className })}
        >
          {icon}
          <span data-slot="nav-label" className={styles.label()}>
            {label}
          </span>
          {count !== undefined && (
            <Badge tone={showActive ? 'brand' : 'neutral'} data-slot="nav-count">
              {count}
            </Badge>
          )}
          <CaretDown
            aria-hidden
            data-slot="nav-chevron"
            className={cn(styles.chevron(), !open && '-rotate-90')}
          />
        </button>
        <ul id={listId} hidden={!open} className="flex flex-col gap-0.5">
          <PlacementContext value="list">{children}</PlacementContext>
        </ul>
      </div>
    </ListItem>
  );
}

export type NavSubItemProps = Omit<ComponentProps<'a'>, 'children'> & {
  /** The page you are on: indicator on the tree line, SemiBold, `aria-current`. */
  active?: boolean;
  /** A small Badge after the label, as on Nav Item (Brand when Active), e.g. how many are running. */
  count?: ReactNode;
  asChild?: boolean;
  children: ReactNode;
};

const subItem =
  'group/sub relative flex h-8 w-full items-center rounded-md pr-2 pl-11 outline-none ' +
  'text-body-sm text-text-secondary cursor-pointer transition-colors duration-[120ms] ease-out ' +
  'hover:bg-bg-muted hover:text-text-primary ' +
  'data-active:text-body-sm-strong data-active:text-text-primary ' +
  'focus-visible:ring-2 focus-visible:ring-border-focus focus-visible:ring-inset';

/**
 * Figma Nav Sub Item (61:65): a nested page, 32 tall, indented 44 so its text
 * lines up with the parent's label. A 1px border/default tree line runs at
 * x=22; Active adds a 2×16 brand/strong indicator on it and Body/Small Strong.
 */
export function NavSubItem({
  active = false,
  count,
  asChild = false,
  className,
  children,
  ...props
}: NavSubItemProps) {
  const placement = useContext(PlacementContext);
  const current = active ? ('page' as const) : undefined;
  const hasCount = count !== undefined && count !== null && count !== false;
  const badge = hasCount && (
    <Badge
      tone={active ? 'brand' : 'neutral'}
      data-slot="nav-count"
      className="ml-auto shrink-0"
    >
      {count}
    </Badge>
  );
  if (placement === 'menu') {
    return (
      <DropdownMenuItem asChild data-active={active || undefined}>
        {asChild && isValidElement<{ children?: ReactNode }>(children) ? (
          cloneElement(
            children as ReactElement<{ 'aria-current'?: 'page'; children?: ReactNode }>,
            { 'aria-current': current },
            children.props.children,
            badge,
          )
        ) : (
          <a aria-current={current} {...props}>
            {children}
            {badge}
          </a>
        )}
      </DropdownMenuItem>
    );
  }
  const child =
    asChild && isValidElement<{ children?: ReactNode }>(children) ? children : null;
  const inner = (label: ReactNode) => (
    <>
      <span
        aria-hidden
        data-slot="nav-guide"
        className="bg-border-default absolute inset-y-0 left-[22px] w-px"
      />
      <span
        aria-hidden
        data-slot="nav-indicator"
        className="bg-brand-strong absolute top-2 left-[21px] hidden h-4 w-0.5 rounded-[1px] group-data-active/sub:block"
      />
      <span data-slot="nav-label" className={cn('min-w-0 truncate', hasCount && 'mr-2')}>
        {label}
      </span>
      {badge}
    </>
  );
  const shared = {
    'data-slot': 'nav-sub-item',
    'data-active': active || undefined,
    'aria-current': current,
    className: cn(subItem, className),
  };
  return (
    <ListItem>
      {child ? (
        <Slot.Root {...shared} {...props}>
          {cloneElement(child, undefined, inner(child.props.children))}
        </Slot.Root>
      ) : (
        <a {...shared} {...props}>
          {inner(children)}
        </a>
      )}
    </ListItem>
  );
}

export type WorkspaceSwitcherProps = {
  /** Figma "Workspace" name (Label/Medium). */
  name: string;
  /** Figma "Plan" line (Caption text/tertiary), e.g. "Pro · 6 members". */
  plan?: ReactNode;
  /** The 32px mark; defaults to the Reelplicate logo mark. */
  logo?: ReactNode;
  collapsed?: boolean;
  className?: string;
  /** Menu content: a `MenuRadioGroup` of workspaces, then actions. */
  children: ReactNode;
};

/**
 * Figma Workspace Switcher (61:77), top of the sidebar: the logo mark, the
 * workspace name and plan and a caret, opening a Menu of workspaces.
 * Collapsed: the mark only (40×40), the name in a right Tooltip.
 */
export function WorkspaceSwitcher({
  name,
  plan,
  logo,
  collapsed: collapsedProp,
  className,
  children,
}: WorkspaceSwitcherProps) {
  const collapsed = useCollapsed(collapsedProp);
  return (
    <Menu>
      <RailTooltip collapsed={collapsed} content={name}>
        <MenuTrigger asChild>
          <button
            type="button"
            data-slot="workspace-switcher"
            aria-label={collapsed ? `Workspace: ${name}` : undefined}
            className={cn(
              'flex h-12 w-full shrink-0 cursor-pointer items-center gap-3 rounded-md px-2 text-left outline-none',
              'transition-colors duration-[120ms] ease-out hover:bg-bg-muted data-[state=open]:bg-bg-muted',
              'focus-visible:ring-2 focus-visible:ring-border-focus focus-visible:ring-inset',
              collapsed && 'size-10 justify-center gap-0 p-0',
              className,
            )}
          >
            {logo ?? (
              <Logo
                variant="mark"
                alt=""
                width={32}
                height={32}
                data-slot="workspace-mark"
                className="size-8 rounded-sm"
              />
            )}
            {!collapsed && (
              <>
                <span className="flex min-w-0 flex-1 flex-col">
                  <span
                    data-slot="workspace-name"
                    className="text-label-md text-text-primary truncate"
                  >
                    {name}
                  </span>
                  {plan && (
                    <span
                      data-slot="workspace-plan"
                      className="text-caption text-text-tertiary truncate"
                    >
                      {plan}
                    </span>
                  )}
                </span>
                <CaretDown
                  aria-hidden
                  data-slot="workspace-chevron"
                  className="text-icon-tertiary size-4 shrink-0"
                />
              </>
            )}
          </button>
        </MenuTrigger>
      </RailTooltip>
      <MenuContent side={collapsed ? 'right' : 'bottom'} align="start">
        {children}
      </MenuContent>
    </Menu>
  );
}

export type SidebarUserProps = {
  /** Figma "Name" (Body/Small Strong). */
  name: string;
  /** Figma "Email" (Caption text/tertiary). */
  email?: string;
  /** Avatar photo; falls back to initials. */
  src?: string;
  /** Presence dot on the Avatar (Figma: online). */
  status?: AvatarStatus;
  collapsed?: boolean;
  className?: string;
  /** Account Menu content (profile, settings, sign out). */
  children: ReactNode;
};

/**
 * Figma Sidebar User (61:96), bottom of the sidebar: a 32px Avatar with
 * presence, name and email, and a Ghost Icon Button (dots-three) opening the
 * account Menu. Collapsed: the avatar alone is the menu button (40×40).
 */
export function SidebarUser({
  name,
  email,
  src,
  status = 'online',
  collapsed: collapsedProp,
  className,
  children,
}: SidebarUserProps) {
  const collapsed = useCollapsed(collapsedProp);
  const menu = (trigger: ReactElement) => (
    <Menu>
      <RailTooltip collapsed={collapsed} content={name}>
        <MenuTrigger asChild>{trigger}</MenuTrigger>
      </RailTooltip>
      <MenuContent side={collapsed ? 'right' : 'top'} align="end">
        {children}
      </MenuContent>
    </Menu>
  );
  if (collapsed) {
    return menu(
      <button
        type="button"
        data-slot="sidebar-user"
        aria-label={`Account menu, ${name}`}
        className={cn(
          'flex size-10 shrink-0 cursor-pointer items-center justify-center rounded-md outline-none',
          'transition-colors duration-[120ms] ease-out hover:bg-bg-muted data-[state=open]:bg-bg-muted',
          'focus-visible:ring-2 focus-visible:ring-border-focus focus-visible:ring-inset',
          className,
        )}
      >
        <Avatar size={32} name={name} src={src} status={status} alt="" />
      </button>,
    );
  }
  return (
    <div
      data-slot="sidebar-user"
      className={cn('flex h-13 w-full shrink-0 items-center gap-3 pl-2', className)}
    >
      <Avatar size={32} name={name} src={src} status={status} />
      <div className="flex min-w-0 flex-1 flex-col">
        <span
          data-slot="sidebar-user-name"
          className="text-body-sm-strong text-text-primary truncate"
        >
          {name}
        </span>
        {email && (
          <span
            data-slot="sidebar-user-email"
            className="text-caption text-text-tertiary truncate"
          >
            {email}
          </span>
        )}
      </div>
      {menu(<IconButton icon={DotsThree} size="sm" aria-label="Account menu" />)}
    </div>
  );
}
